import type { FullClientRuntimeConfig } from "core/utils/getClientRuntimeConfig";
import { createContext, type ReactNode, useContext } from "react";

type SharedConfiguration = Pick<
  FullClientRuntimeConfig,
  "API_URL" | "APPLICATIONINSIGHTS_CONNECTION_STRING" | "SUPPORT_EMAIL"
>;
export type PublicAppConfiguration = SharedConfiguration & {
  NEXT_PUBLIC_LOGO_BASE_URL: string | undefined;
  NEXT_PUBLIC_RECAPTCHA_SITE_KEY: string | undefined;
};

const PitstopConfigurationContext =
  createContext<FullClientRuntimeConfig | null>(null);
const PublicConfigurationContext = createContext<PublicAppConfiguration | null>(
  null
);

export function PitstopConfigurationProvider({
  children,
  config,
}: {
  children: ReactNode;
  config: FullClientRuntimeConfig;
}) {
  return (
    <PitstopConfigurationContext.Provider value={config}>
      {children}
    </PitstopConfigurationContext.Provider>
  );
}

export function PublicConfigurationProvider({
  children,
  config,
}: {
  children: ReactNode;
  config: PublicAppConfiguration;
}) {
  return (
    <PublicConfigurationContext.Provider value={config}>
      {children}
    </PublicConfigurationContext.Provider>
  );
}

export function useAppConfiguration():
  | FullClientRuntimeConfig
  | PublicAppConfiguration {
  const pitstopConfig = useContext(PitstopConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  if (pitstopConfig) {
    return pitstopConfig;
  }
  if (publicConfig) {
    return publicConfig;
  }
  throw new Error("Application configuration provider is missing");
}

export function usePitstopConfiguration() {
  const config = useContext(PitstopConfigurationContext);
  if (!config) {
    throw new Error("Pitstop configuration provider is missing");
  }
  return config;
}

export function useIsPitstopConfiguration() {
  return useContext(PitstopConfigurationContext) !== null;
}

export function useRecaptchaSiteKey() {
  const pitstopConfig = useContext(PitstopConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  if (pitstopConfig) {
    return pitstopConfig.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  }
  if (publicConfig) {
    return publicConfig.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  }
  throw new Error("Application configuration provider is missing");
}
